import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useSelector } from 'react-redux';
import { OverlayPanel } from "primereact/overlaypanel";

import { getProfile } from "../../config/global";
import "./css/Menu.css";

import { useLogoutMutation } from "../../queries/auth";
import { useIsRestaurant } from "../../hooks/useAccountType";
import AvatarCircle from "../SharedComponents/AvatarCircle";

import EmergencyButton from "../SharedComponents/Emergency/EmergencyButton";
import ThemeToggle from "../SharedComponents/ThemeToggle";
import LanguageSwitcher from "../SharedComponents/LanguageSwitcher";
import LogoMark from "../../components/auth/LogoMark";
import { usePlanState } from "../../utils/planState";
import { useNavV6 } from "../../nav/useNavV6";
import { useEntitlements } from "../../hooks/useEntitlements";
import { buildSidebar } from "../../nav/navModel";
import "../../styles/pp-activation.css";

// Inline nav SVGs — verbatim from .claude/pp-shell-prototype.html (same
// viewBox/paths/stroke-width) for every item with a prototype counterpart;
// same clean single-color line style, hand-matched, for the restaurant-only
// items the prototype doesn't cover. No boxicons/FontAwesome in the nav list.
const Ic = ({ children }) => (
  <svg className="menu-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">{children}</svg>
);
const ICONS = {
  home:        <Ic><path d="M3 11l9-8 9 8" /><path d="M5 10v10h14V10" /></Ic>,
  create:      <Ic><path d="M5 3v4M3 5h4M6 17v4M4 19h4M13 3l3 6 6 3-6 3-3 6-3-6-6-3 6-3z" /></Ic>,
  screens:     <Ic><rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8" /></Ic>,
  content:     <Ic><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></Ic>,
  shows:       <Ic><circle cx="12" cy="12" r="9" /><path d="M10 8l6 4-6 4z" /></Ic>,
  schedule:    <Ic><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></Ic>,
  remote:      <Ic><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" /></Ic>,
  scenes:      <Ic><path d="M2 6h20M2 12h20M2 18h20" /></Ic>,
  apps:        <Ic><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></Ic>,
  customers:   <Ic><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></Ic>,
  suggestions: <Ic><path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2zM9 21h6" /></Ic>,
  marketing:   <Ic><path d="M4 8h4l10-4v16L8 16H4zM18 8a3 3 0 0 1 0 8" /></Ic>,
  loyalty:     <Ic><rect x="3" y="8" width="18" height="13" rx="2" /><path d="M3 12h18M12 8V5a2 2 0 1 0-2 2M12 8V5a2 2 0 1 1 2 2" /></Ic>,
  money:       <Ic><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M16 12h3M3 10h18" /></Ic>,
  // New navigation (prototype v6) only.
  moments:     <Ic><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></Ic>,
  matches:     <Ic><circle cx="12" cy="12" r="9" /><path d="M12 7l4 3-1.5 5h-5L8 10z" /></Ic>,
  // Restaurant-only — no prototype counterpart, same line style, hand-drawn to match.
  menuBuilder: <Ic><path d="M6 2v6a2 2 0 0 0 2 2v12M6 2v6M9 2v6" /><path d="M16 2c0 4-2 6-2 10v10" /></Ic>,
  photo:       <Ic><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></Ic>,
  pin:         <Ic><path d="M12 21s7-7.5 7-12a7 7 0 1 0-14 0c0 4.5 7 12 7 12z" /><circle cx="12" cy="9" r="2.5" /></Ic>,
  gear:        <Ic><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></Ic>,
};

// Brand block — the PopupPush mark (static here; it only animates on the auth
// pages) + wordmark "Popup"+"Push" (Push in honey) + the Arabic subtitle.
const BrandMark = ({ small = false }) => (
  <div className="pp-brand" style={small ? { gap: 8 } : undefined}>
    <LogoMark size={small ? 30 : 38} />
    <div style={{ minWidth: 0 }}>
      <div className="pp-brand-nm" style={small ? { fontSize: 15 } : undefined}>
        <span className="ppa-wordmark" dir="ltr">Popup<b>Push</b></span>
      </div>
      {!small && <div className="pp-brand-sb">شاشات المقاهي والمطاعم</div>}
    </div>
  </div>
);
export { BrandMark };

function MenuList() {
    const { t } = useTranslation();

    const location = useLocation();
    const companyDetails = useSelector((state) => state.global.companyDetails);
    const profileOverlayRef = useRef(null);
    const navigate = useNavigate()
    const [isMenuOpen, setIsMenuOpen] = useState(location.pathname.includes("/campaign"));
    const [pathName, setPathName] = useState("");
    var features = companyDetails?.package?.features
    const isRestaurant = useIsRestaurant();
    const planStatus = usePlanState().status;
    // New navigation (prototype v6): per-company switch, default OFF, never for restaurant accounts.
    const navV6 = useNavV6();
    const entitlements = useEntitlements();

    useEffect(() => {
        const pathname = window.location.pathname;
        const firstSlashIndex = pathname.indexOf('/');
        const secondSlashIndex = pathname.indexOf('/', firstSlashIndex + 1);
        const contentBetweenSlashes = pathname.substring(firstSlashIndex + 1, secondSlashIndex);
        setPathName(contentBetweenSlashes);
    }, [])

    const logout = useLogoutMutation();

    const logoutHandler = () => {
        logout.mutateAsync()
    }

    const subMenuItems = [
        {
            label: t('nav.billing_settings'),
            icon: "pi pi-dollar",
            command: () => navigate("/profile", { state: { activeTab: "billing" } })
        },
        {
            label: t('nav.account_settings'),
            icon: "pi pi-user",
            command: () => navigate("/profile", { state: { activeTab: "account" } })
        },
        {
            label: "Emergency Settings",
            icon: "pi pi-bell",
            command: () => navigate("/profile", { state: { activeTab: "emergency" } })
        },
        { separator: true },
        {
            label: t('nav.logout'),
            icon: "pi pi-sign-out",
            command: () => logoutHandler(),
            className: "text-red-500"
        },
    ]

    // Seven top-level items. Shows is split out of the Content
    // aggregator into its own sidebar entry — Content now hosts only
    // Files / Playlists / Links. Sub-navigation still lives in the
    // SectionChipBar for Content / Schedule / Money.
    const menuItem = [
        {
            path: "/dashboard",
            name: t('nav.home'),
            icon: ICONS.home,
            visible: true,
            matchPrefixes: ['/dashboard'],
            sectionLabel: t('nav.section_admin'),
        },
        {
            // "+ Create" — the café template-picker wizard (net-new front door).
            // Standard/café accounts only; the restaurant menu is a separate list.
            path: "/create",
            name: t('nav.create'),
            icon: ICONS.create,
            visible: true,
            hero: true,
            matchPrefixes: ['/create'],
        },
        {
            // "Screens" is the renamed "Devices" — same page, friendlier label.
            path: "/devices/list",
            name: t('nav.screens'),
            icon: ICONS.screens,
            visible: true,
            matchPrefixes: ['/devices', '/screens'],
        },
        {
            // Content aggregator → Files / Playlists / Links.
            path: "/content",
            name: t('nav.content'),
            icon: ICONS.content,
            visible: true,
            matchPrefixes: ['/content', '/media', '/playlist', '/links'],
        },
        {
            // Shows — formerly a chip under Content. Reuses the existing
            // /campaigns routes (ShowsGallery / campaign setup).
            path: "/campaigns/list",
            name: t('nav.shows'),
            icon: ICONS.shows,
            visible: true,
            matchPrefixes: ['/campaigns', '/interactive'],
        },
        {
            // Schedule aggregator → Matches / Birthday / Happy Hour / Prayer.
            path: "/schedule",
            name: t('nav.schedule'),
            icon: ICONS.schedule,
            visible: true,
            matchPrefixes: ['/schedule', '/apps/match-scheduler', '/apps/birthday', '/apps/prayer', '/apps/happy-hour', '/apps/promos', '/apps/spin-win'],
        },
        {
            // Hero — gets a subtle accent via .pp-menu-hero.
            path: "/remote-control",
            name: t('nav.remote_control'),
            icon: ICONS.remote,
            visible: true,
            hero: true,
            matchPrefixes: ['/remote-control'],
        },
        {
            // Scenes — user-friendly one-tap presets the venue builds from the
            // devices the admin set up. Device/receiver/screen SETUP is admin-
            // only now, so the old technical "Hardware" entry is gone; the
            // venue drives everything from Remote Control + Scenes.
            path: "/hardware/scenes",
            name: t('nav.scenes'),
            icon: ICONS.scenes,
            visible: true,
            matchPrefixes: ['/hardware/scenes'],
        },
        {
            // Apps directory — every Layout / Animated / Weather / Clock / RSS /
            // 3D / Broadcast page in one grid. Discoverability over hiding —
            // every app shows, FeatureLocked page-level handles the upsell.
            path: "/apps",
            name: t('nav.apps'),
            icon: ICONS.apps,
            visible: true,
            matchPrefixes: ['/apps/layouts', '/apps/animated_layouts', '/apps/weather', '/apps/clock', '/apps/rss', '/apps/3d', '/apps/broadcast'],
        },
        {
            // Customers — Smart CRM directory (birthday/spin capture + manual + import).
            path: "/customers",
            name: t('nav.customers'),
            icon: ICONS.customers,
            visible: true,
            matchPrefixes: ['/customers'],
            sectionLabel: t('nav.section_business'),
        },
        {
            // Suggestions — Customer 360 automation feed (owner approves actions).
            path: "/automation",
            name: t('nav.suggestions'),
            icon: ICONS.suggestions,
            visible: true,
            matchPrefixes: ['/automation'],
        },
        {
            // Marketing — Smart CRM campaigns (email blasts to captured customers).
            path: "/marketing/campaigns",
            name: t('nav.marketing'),
            icon: ICONS.marketing,
            visible: true,
            matchPrefixes: ['/marketing'],
        },
        {
            // Loyalty — QR check-in, rewards, featured items ("Scan & Stay"). Always
            // visible (discoverability); the pages FeatureLocked-gate when
            // qr_loyalty is off, driving the upgrade — same as Apps/Marketing.
            path: "/loyalty/settings",
            name: t('nav.loyalty'),
            icon: ICONS.loyalty,
            visible: true,
            matchPrefixes: ['/loyalty'],
        },
        {
            // Money aggregator → Plan / Earnings / Ad Settings / Marketplace.
            path: "/money",
            name: t('nav.money'),
            icon: ICONS.money,
            visible: true,
            matchPrefixes: ['/money', '/billing', '/apps/ad-marketplace', '/apps/smart-ad-scheduler', '/profile'],
        },
    ];

    // Restaurant (video-wall-only) sidebar — a curated subset focused on the wall
    // workflow. Standard accounts keep the full `menuItem` list untouched. The
    // route guard (CompanyDetailsRouter) enforces these limits server-of-truth-side
    // too, so a hidden item can't be reached by URL either.
    const restaurantMenu = [
        {
            path: "/apps/video-wall",
            name: t('nav.vw_dashboard'),
            icon: ICONS.home,
            visible: true,
            exact: true,
            matchPrefixes: ['/apps/video-wall'],
        },
        {
            path: "/apps/video-wall/walls",
            name: t('nav.vw_my_walls'),
            icon: ICONS.screens,
            visible: true,
            matchPrefixes: ['/apps/video-wall/walls', '/apps/video-wall/groups', '/apps/video-wall/locations'],
        },
        {
            path: "/apps/video-wall/menu-builder",
            name: t('nav.vw_menu_builder'),
            icon: ICONS.menuBuilder,
            visible: true,
            matchPrefixes: ['/apps/video-wall/menu-builder', '/apps/video-wall/menus', '/apps/video-wall/templates'],
        },
        {
            path: "/apps/video-wall/promos",
            name: t('nav.vw_promos'),
            icon: ICONS.marketing,
            visible: true,
            matchPrefixes: ['/apps/video-wall/promos'],
        },
        {
            path: "/apps/video-wall/library",
            name: t('nav.vw_library'),
            icon: ICONS.content,
            visible: true,
            matchPrefixes: ['/apps/video-wall/library'],
        },
        {
            // Links to the existing media gallery/upload page (same as standard
            // users' Content → Files). Bare /media has no route — /media/gallery is
            // the real page. Guard allows the /media prefix for restaurant accounts.
            path: "/media/gallery",
            name: t('nav.vw_my_media'),
            icon: ICONS.photo,
            visible: true,
            matchPrefixes: ['/media'],
        },
        {
            // Links to the existing devices page (pair / online-offline / manage).
            // Same page standard users get; guard allows the /devices prefix.
            path: "/devices/list",
            name: t('nav.vw_my_devices'),
            icon: ICONS.screens,
            visible: true,
            matchPrefixes: ['/devices', '/screens'],
        },
        {
            path: "/apps/video-wall/schedule",
            name: t('nav.schedule'),
            icon: ICONS.schedule,
            visible: true,
            matchPrefixes: ['/apps/video-wall/schedule'],
        },
        {
            // Branches/Locations as a first-class sidebar item (not buried in Video
            // Wall). Clean page at /schedule/locations (guard now allows /schedule).
            path: "/schedule/locations",
            name: t('video_wall.locations.title'),
            icon: ICONS.pin,
            visible: true,
            matchPrefixes: ['/schedule/locations'],
        },
        {
            path: "/customers",
            name: t('nav.customers'),
            icon: ICONS.customers,
            visible: true,
            matchPrefixes: ['/customers'],
        },
        {
            path: "/automation",
            name: t('nav.suggestions'),
            icon: ICONS.suggestions,
            visible: true,
            matchPrefixes: ['/automation'],
        },
        {
            // Marketing — Smart CRM campaigns (restaurant accounts too, Step 7).
            path: "/marketing/campaigns",
            name: t('nav.marketing'),
            icon: ICONS.marketing,
            visible: true,
            matchPrefixes: ['/marketing'],
        },
        {
            path: "/profile",
            name: t('nav.vw_settings'),
            icon: ICONS.gear,
            visible: true,
            matchPrefixes: ['/profile', '/money', '/billing'],
        },
    ];

    // New navigation: the sidebar is generated from the package (useEntitlements: only an explicit false hides).
    // Waits for the company details so it never flashes an empty menu; OFF returns today's list untouched.
    const newMenu = (navV6 && entitlements.ready)
        ? buildSidebar({ has: entitlements.has, features: entitlements.features }, (k) => t(k))
            .map((it) => ({ ...it, icon: ICONS[it.iconKey] ?? ICONS.apps }))
        : null;

    const menuToRender = isRestaurant ? restaurantMenu : (newMenu ?? menuItem);

    const NavbarLink = ({ item, index, subItem = false }) => {
        if (!item.visible) return null;
        // Active when current URL matches any of the item's prefixes — so
        // /campaigns/list still highlights "Content" (which owns the prefix).
        const currentPath = location.pathname;
        const prefixes = item.matchPrefixes ?? [item.path];
        // exact: highlight only on the exact path (used by the restaurant Dashboard,
        // whose path is a prefix of every other video-wall route).
        const isActive = item.exact
            ? prefixes.some(p => currentPath === p)
            : prefixes.some(p => currentPath === p || currentPath.startsWith(p + '/'))
                || (item.exactPaths ?? []).includes(currentPath);
        return (
            <NavLink to={item.path} end={item.end} key={`${index}-${item.name}`}
                className={`menu-item ${isActive ? 'active' : ''} ${item.hero ? 'pp-menu-hero' : ''}`}
            >
                <li className="d-flex flex-row p-2 menu-link">
                    <div>{item.icon}</div>
                    <div>{item.name}</div>
                    {item.count ? <span className="pp-nav-count" style={{ marginInlineStart: 'auto', opacity: 0.7, fontSize: '0.75rem' }}>{item.count}</span> : null}
                </li>
            </NavLink>
        );
    }

    const SubMenu = ({ item, index }) => {
        if (!item.visible) return null;
        return (
            <>
                <div onClick={() => setIsMenuOpen(!isMenuOpen)} key={`${index}-${item.name}`}
                    className={`menu-item ${pathName == item.name.toLocaleLowerCase() && ("submenu" in item) ? "active" : ""}`}>
                    <li className="d-flex flex-column align-items-start p-2 menu-link cursor-pointer" >
                        <div className="d-flex flex-row  w-100">
                            <div >{item.icon}</div>
                            <div className="d-flex justify-content-between align-items-center w-100"  >
                                <span>{item.name}</span>
                                {
                                    isMenuOpen ?
                                        <i className="fas fa-chevron-down fs-10"></i>
                                        :
                                        <i className="fas fa-angle-right fs-10"></i>
                                }
                            </div>
                        </div>
                    </li>
                </div>
                {
                    isMenuOpen &&
                    <ul className="ps-3 d-block w-100 flex-grow-0 h-auto  menu-inner">
                        {
                            item.submenu.filter(sub => sub.visible).map((item, index) => {
                                return item.visible && <NavbarLink item={item} index={index} subItem={true} />
                            })
                        }
                    </ul>
                }
            </>
        )
    }

    return (
        <>
            <div className="app-brand demo">
                <Link to="/dashboard" className="app-brand-link" style={{ margin: "auto" }}>
                    <BrandMark />
                </Link>
            </div>

            {/* Venue context chip (shell migration) — real company name; the
                second line is the prototype's own static "الحساب النشط"
                label (no real "branch" field exists on the company record,
                so unlike an earlier draft this isn't trying to show one).
                No chevron: the prototype's implied location-switcher has no
                real multi-location switch behind it for standard accounts. */}
            {companyDetails?.company?.name && (
                <div className="pp-venue-chip">
                    <div className="pp-venue-chip-icon">
                        {companyDetails.company.logo
                            ? <img src={companyDetails.company.logo} alt="" className="pp-venue-chip-logo" />
                            : companyDetails.company.name.charAt(0)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div className="pp-venue-chip-name">{companyDetails.company.name}</div>
                        <div className="pp-venue-chip-badge">
                            {planStatus === 'active' ? t('nav.venue_active_account') : t(`activation.${planStatus}.venue_state`)}
                        </div>
                    </div>
                    {planStatus !== 'active' && <span className="ppn-pill">{t(`activation.${planStatus}.pill`)}</span>}
                </div>
            )}

            <div className="menu-inner-shadow"></div>

            {/* =============== Menu Sorting Code =============== */}
            <ul className="menu-inner">
                {menuToRender.filter(i => i.visible).map((Item, index) => {
                    // Stable key (was `${index}-${Math.random()}` which regenerated every
                    // render → React remounted EVERY sidebar item on every re-render; on
                    // pages that re-render rapidly, e.g. the wall detail page's RTDB
                    // telemetry, that tore items down mid-click and ate navigation clicks).
                    const key = `${index}-${Item.name}`;
                    return (
                        <React.Fragment key={key}>
                            {Item.sectionLabel && <div className="pp-nav-section-label">{Item.sectionLabel}</div>}
                            {"submenu" in Item ?
                                <SubMenu item={Item} index={index} />
                                :
                                <NavbarLink item={Item} index={index} />}
                        </React.Fragment>
                    );

                })}
            </ul>

            {/* Phone-only (<768px) settings — language relocated here from the
                top header so the phone navbar stays uncluttered. Theme now
                lives in the always-visible footer row below (shell
                migration) instead of duplicating it here. Hidden ≥768px
                (d-md-none) where the header shows it inline.
                justify-content-between mirrors correctly in RTL. */}
            <div className="d-md-none px-3 py-2 pp-drawer-settings">
                <div className="d-flex align-items-center justify-content-between gap-2 rounded p-2 pp-drawer-setting-row">
                    <span className="d-flex align-items-center gap-2 fw-semibold" style={{ fontSize: '0.85rem' }}>
                        <i className="pi pi-globe" /> {t('language.switch')}
                    </span>
                    <LanguageSwitcher className="pp-drawer-lang" />
                </div>
            </div>

            {/* Footer icon row (shell migration) — theme toggle + Emergency
                Stop as compact honey square buttons, matching the
                prototype's .fbtn row. Always visible (same reachability the
                old labeled Emergency Stop row had), replacing that row and
                consolidating theme toggle to this ONE touchpoint instead of
                three (mobile drawer block, this row, desktop popover). */}
            <div className="px-3 py-1 pp-side-foot-row">
                <ThemeToggle />
                <span className="pp-side-foot-estop">
                    <EmergencyButton icon="pi pi-shield" />
                </span>
            </div>

            {/* Profile row (≥1200px only — at xl the desktop sidebar REPLACES the
                top navbar, which is display:none ≥1200, so this is the only place
                language/theme/logout can live at that width). One row now opens a
                popover holding language, theme and account/logout together —
                the Slack/Linear/Notion pattern: settings set once and rarely
                touched again shouldn't hold permanent space against the nav list.
                Below xl the top navbar already carries all of this, so nothing
                is lost — only de-duplicated. */}
            <div className="d-none d-xl-block pp-profile-row-wrap">
                <div
                    className="d-flex align-items-center gap-2 p-2 rounded cursor-pointer pp-profile-row"
                    onClick={(e) => profileOverlayRef.current.toggle(e)}
                >
                    {/* Circular avatar (border-radius:50%) — AvatarCircle's own
                        fallback (initials) already rounds itself inline, but
                        the real-photo <img> path only rounds when it's given
                        a class that does; this callsite passed none before,
                        so a venue with a real uploaded logo rendered a
                        square image here. */}
                    <AvatarCircle className="rounded-circle" style={{ width: 34, height: 34 }} />
                    <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
                        <span className="pp-user-name">{getProfile().name.length > 15 ? getProfile().name.slice(0, 20) + "..." : getProfile().name}</span>
                        {/* Plan label, not email — matches the prototype's .user
                            .ue slot (the demo's "الباقة الاحترافية"). Mapped off
                            `companyDetails.package.is_free` (a real boolean the
                            backend already sends and CompanyDetailsRouter already
                            trusts for the same free/paid distinction), NOT
                            `companyDetails.package.title` — that field is raw,
                            backend-authored text and on this dev company reads
                            "Pro (jacky ubitc dev test — venue_overlay_promos)",
                            a package the platform team named for a specific test
                            scenario. Falls back to the account name (as before)
                            only when there's no package data at all. */}
                        <span className="pp-user-plan">
                            {companyDetails?.package
                                ? (companyDetails.package.is_free ? t('nav.plan_free') : t('nav.plan_pro'))
                                : getProfile().name}
                        </span>
                    </div>
                    <i className="pi pi-ellipsis-v" />
                </div>

                <OverlayPanel ref={profileOverlayRef} className="pp-profile-popover" appendTo={typeof document !== 'undefined' ? document.body : undefined}>
                    <div className="pp-drawer-setting-row d-flex align-items-center justify-content-between gap-2 rounded p-2">
                        <span className="d-flex align-items-center gap-2 fw-semibold" style={{ fontSize: '0.85rem' }}>
                            <i className="pi pi-globe" /> {t('language.switch')}
                        </span>
                        <LanguageSwitcher className="pp-drawer-lang" />
                    </div>
                    <div className="pp-profile-popover-divider" />
                    {subMenuItems.map((item, index) => item.separator ? (
                        <div className="pp-profile-popover-divider" key={`sep-${index}`} />
                    ) : (
                        <button
                            key={item.label}
                            type="button"
                            className={`pp-profile-popover-item ${item.className ?? ''}`}
                            onClick={() => { item.command(); profileOverlayRef.current.hide(); }}
                        >
                            <i className={item.icon} /> {item.label}
                        </button>
                    ))}
                </OverlayPanel>
            </div>

        </>
    )
}

export default MenuList